/**
 * USP bar (FIL001-26): moves on to the next USP every few seconds, and tells the header how far down it sits.
 *
 * The rotation is the quote carousel's (filliers-quote.js). It waits while the bar is scrolled away and while the
 * tab is hidden, pauses while a pointer or keyboard focus is on the bar and for the block the theme editor has
 * selected, and never starts under `prefers-reduced-motion`. There are no dots, so nothing stops it for good.
 *
 * The offset. The bar scrolls away above a sticky header, so at the top of the page the header is one bar lower
 * than its overlays assume: the nav scrim and the search panel reach up to what they take for the viewport's top
 * edge, and the nav panel is sized to end 4rem above its bottom. `--fil-header-offset` on <html> is the header's
 * distance from the top of the viewport — the bar's height at the top of the page, 0 once the header sticks — and
 * filliers-header.css and filliers-nav.css subtract it. It is written only when it changes, so once the header
 * sticks a scroll costs one rect read and no style work.
 */
if (!customElements.get('filliers-announcement-bar')) {
  class FilliersAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.requestMeasure = this.requestMeasure.bind(this);
      window.addEventListener('scroll', this.requestMeasure, { passive: true });
      window.addEventListener('resize', this.requestMeasure);
      this.measure();

      this.slides = [...this.querySelectorAll('.filliers-announcement__slide')];
      if (this.slides.length < 2) return;

      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active')),
      );
      this.interval = (parseFloat(this.dataset.interval) || 10) * 1000;
      this.stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hovered = false;
      this.focused = false;
      this.pinned = false;
      this.visible = false;

      this.schedule = this.schedule.bind(this);

      this.addEventListener('mouseenter', () => this.setHold('hovered', true));
      this.addEventListener('mouseleave', () => this.setHold('hovered', false));
      this.addEventListener('focusin', () => this.setHold('focused', true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setHold('focused', false);
      });

      // Theme editor: selecting a USP block shows it and holds it there until it is deselected.
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('.filliers-announcement__slide'));
        if (index < 0) return;
        this.show(index);
        this.setHold('pinned', true);
      });
      this.addEventListener('shopify:block:deselect', () => this.setHold('pinned', false));

      document.addEventListener('visibilitychange', this.schedule);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.visible = entry.isIntersecting;
            this.schedule();
          },
          { threshold: 0.5 },
        );
        this.observer.observe(this);
      } else {
        this.visible = true;
        this.schedule();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.requestMeasure);
      window.removeEventListener('resize', this.requestMeasure);
      cancelAnimationFrame(this.frame);
      document.documentElement.style.removeProperty('--fil-header-offset');

      clearTimeout(this.timer);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.schedule);
    }

    requestMeasure() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    // Looked up each time: the theme editor replaces the header's section when one of its settings changes.
    measure() {
      const header = document.querySelector('.shopify-section-header');
      const offset = header ? Math.max(0, Math.round(header.getBoundingClientRect().top)) : 0;
      if (offset === this.offset) return;
      this.offset = offset;
      document.documentElement.style.setProperty('--fil-header-offset', `${offset}px`);
    }

    setHold(reason, value) {
      this[reason] = value;
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.stopped || this.hovered || this.focused || this.pinned || !this.visible || document.hidden) return;

      this.timer = setTimeout(() => {
        this.show((this.index + 1) % this.slides.length);
        this.schedule();
      }, this.interval);
    }

    show(index) {
      this.index = index;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    }
  }

  customElements.define('filliers-announcement-bar', FilliersAnnouncementBar);
}
